import React, { useMemo } from "react";
import {
    Autocomplete,
    TextField,
    Box,
    Typography,
    Checkbox
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useControlledAutocomplete } from "../hooks";

export interface AutocompleteOption {
    id: string | number;
    label?: string;
    project_reference?: string;
    [key: string]: any;
}

interface PortfolioProjectsAutocompleteProps {
    value: AutocompleteOption | AutocompleteOption[] | null;
    label?: string;
    placeholder?: string;
    multiSelect?: boolean;
    onSelect: (value: AutocompleteOption | AutocompleteOption[] | null, isChoosedAll: boolean) => void;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    required?: boolean;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const PortfolioProjectsAutocomplete: React.FC<PortfolioProjectsAutocompleteProps> = ({
    value,
    label = "Search",
    placeholder = "Type to search...",
    multiSelect = false,
    onSelect,
    disabled = false,
    error = false,
    helperText,
    required = false,
}) => {
    const { options, loading, onChange, onInputChange } =
        useControlledAutocomplete("SEARCH_PORTFOLIO_PROJECTS");

    const getDisplayText = (option: AutocompleteOption) => {
        return option.description || option.name || option.label || "";
    };

    const togglePortfolioProjects = (portfolio: any) => {
        if (!Array.isArray(value) || !value) {
            // If no selection yet, select all projects from this portfolio
            const projects = (portfolio.projects || []).map((p: any) => ({
                ...p,
                id: p.project_reference || p.id,
                label: p.title,
            }));
            onSelect(projects.length ? projects : null, true);
            return;
        }

        const projectIds = new Set(
            portfolio.projects?.map(
                (p: any) => p.project_reference || p.id
            ) || []
        );

        const existing = value.filter(v => projectIds.has(v.id));
        if (!existing?.length && projectIds.size === 0) {
            return;
        }
        const shouldSelectAll = existing?.length !== projectIds.size;

        const updatedValue = shouldSelectAll
            ? [
                ...(value || []),
                ...(portfolio.projects || []).map((p: any) => ({
                    ...p,
                    id: p.project_reference || p.id,
                    label: p.title,
                })),
            ]
            : (value || []).filter(v => !projectIds.has(v.id));

        // remove duplicates
        const unique = Array.from(
            new Map(updatedValue.map(i => [i.id, i])).values()
        );

        onSelect(unique?.length ? unique : null, true);
    };

    const structuredOptions = React.useMemo(() => {
        const flattened: any[] = [];

        options.forEach((portfolio: any) => {
            const portfolioId = portfolio.portfolio_id || portfolio.id;

            // Always push portfolio itself
            flattened.push({
                ...portfolio,
                id: portfolioId,
                isPortfolio: true,
                label: portfolio.portfolio_name,
                portfolioName: portfolio.portfolio_name,
            });

            // Only push projects if they exist
            if (Array.isArray(portfolio.projects)) {
                portfolio.projects.forEach((project: any) => {
                    flattened.push({
                        ...project,
                        id: project.project_reference || project.id,
                        label: project.title,
                        portfolioId,
                        portfolioName: portfolio.portfolio_name,
                    });
                });
            }
        });

        return flattened;
    }, [options]);

    /** -------------------------------
     * SELECTION HANDLING
     * -------------------------------- */
    const handleSelect = (newValue: any) => {
        const selectedArray = Array.isArray(newValue)
            ? newValue
            : newValue
                ? [newValue]
                : [];

        let finalSelection: AutocompleteOption[] = [];

        selectedArray.forEach((item: any) => {
            if (item.isPortfolio) {
                const portfolio = options.find(
                    (p: any) => (p.portfolio_id || p.id) === item.id
                );

                portfolio?.projects?.forEach((project: any) => {
                    finalSelection.push({
                        ...project,
                        id: project.project_reference || project.id,
                        label: project.title,
                    });
                });
            } else {
                finalSelection.push(item);
            }
        });

        // Remove duplicates
        const unique = Array.from(
            new Map(finalSelection.map((i) => [i.id, i])).values()
        );

        onSelect(multiSelect ? unique : unique[0] ?? null, false);
    };

    return (
        <Autocomplete
            disabled={disabled}
            multiple={multiSelect}
            disableCloseOnSelect={multiSelect}
            loading={loading}
            value={multiSelect ? (Array.isArray(value) ? value : []) : value}
            options={structuredOptions}
            onChange={(_, newValue) => {
                onChange(
                    "SEARCH_PORTFOLIO_PROJECTS",
                    Array.isArray(newValue) ? newValue[0] ?? null : newValue
                );
                handleSelect(newValue);
            }}
            onInputChange={onInputChange}
            getOptionLabel={(option) => option.label || getDisplayText(option)}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    required={required}
                />
            )}
            renderOption={(props, option: any, { selected }) => (
                <Box
                    component="li"
                    {...props}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        pl: option.isPortfolio ? 2 : 4,
                        py: 1,
                        fontWeight: option.isPortfolio ? 600 : 400,
                    }}
                >
                    {option.isPortfolio && multiSelect && (
                        <Box display="flex" alignItems="center" mr="auto" mb={1}>
                            <Typography
                                variant="subtitle2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePortfolioProjects(option);
                                }}
                            >
                                {option.label || getDisplayText(option)}
                            </Typography>
                        </Box>
                    )}

                    {!option.isPortfolio && multiSelect && (
                        <Box display="flex" alignItems="center" mr="auto" pl={2}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                checked={selected}
                                sx={{ mr: 1 }}
                            />
                            <Typography variant="body2">
                                {option.label || getDisplayText(option)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
            sx={{ width: "100%" }}
        />
    );
};
